import type { PublicUser, UserRole } from '@flame/shared';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useLocation, useRoute } from 'preact-iso';
import { api, errorMessage } from '../api';
import { Field, Spinner } from '../components/ui';
import { registerPasskey, signInWithPasskey } from '../lib/passkey';
import { loadAuthState, needsSetup, user } from '../state';

function AuthCard({ title, children }: { title: string; children: ComponentChildren }) {
  return (
    <main class="auth">
      <div class="auth-card">
        <img src="/favicon.svg" alt="" width={40} height={40} />
        <h1>{title}</h1>
        {!browserSupportsWebAuthn() && (
          <p class="error">This browser doesn't support passkeys, so you can't sign in here.</p>
        )}
        {children}
      </div>
    </main>
  );
}

/** Shared busy/error handling for the passkey buttons. */
function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, run };
}

async function finishSignIn(
  route: (path: string, replace?: boolean) => void,
  signedIn: PublicUser
) {
  user.value = signedIn;
  await loadAuthState();
  route('/', true);
}

export function Login() {
  const { route } = useLocation();
  const { busy, error, run } = useAction();
  return (
    <AuthCard title="Sign in">
      {needsSetup.value ? (
        <p>
          This start page isn't set up yet. Open the setup link printed in the server logs to
          create the first account.
        </p>
      ) : (
        <>
          <p class="muted">Use the passkey saved on this device, or scan a QR code with your phone.</p>
          <button
            type="button"
            class="button button-primary button-large"
            disabled={busy}
            onClick={() => run(async () => finishSignIn(route, await signInWithPasskey()))}
          >
            {busy ? 'Waiting for passkey…' : 'Sign in with a passkey'}
          </button>
          {error && <p class="error">{error}</p>}
        </>
      )}
    </AuthCard>
  );
}

function NewAccountForm({
  submitLabel,
  onSubmit,
}: {
  submitLabel: string;
  onSubmit: (username: string, displayName: string) => Promise<void>;
}) {
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const { busy, error, run } = useAction();
  return (
    <form
      class="form"
      onSubmit={(e) => {
        e.preventDefault();
        void run(() => onSubmit(username, displayName));
      }}
    >
      <Field label="Your name">
        <input
          required
          autoFocus
          maxLength={64}
          value={displayName}
          onInput={(e) => {
            const v = e.currentTarget.value;
            // Suggest a username from the name until the user edits it.
            if (username === slug(displayName)) setUsername(slug(v));
            setDisplayName(v);
          }}
        />
      </Field>
      <Field label="Username" hint="2–32 characters: a–z, 0–9, _ . -">
        <input
          required
          pattern="[a-zA-Z0-9_.\-]{2,32}"
          value={username}
          onInput={(e) => setUsername(e.currentTarget.value)}
          autoCapitalize="none"
          autoComplete="username webauthn"
        />
      </Field>
      <button type="submit" class="button button-primary button-large" disabled={busy}>
        {busy ? 'Waiting for passkey…' : submitLabel}
      </button>
      {error && <p class="error">{error}</p>}
    </form>
  );
}

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9_.-]+/g, '')
    .slice(0, 32);

export function Setup() {
  const { route, query } = useLocation();
  const token = query.token ?? '';
  return (
    <AuthCard title="Set up your start page">
      {!token ? (
        <p class="error">This setup link is missing its token. Copy the full link from the server logs.</p>
      ) : (
        <>
          <p class="muted">
            Create the admin account. You'll sign in with a passkey: your device's fingerprint,
            face, or PIN. There's no password.
          </p>
          <NewAccountForm
            submitLabel="Create passkey"
            onSubmit={async (username, displayName) => {
              const { user: created } = await registerPasskey<{ user: PublicUser }>(
                '/api/auth/setup/options',
                '/api/auth/setup/verify',
                { token, username, displayName }
              );
              await finishSignIn(route, created);
            }}
          />
        </>
      )}
    </AuthCard>
  );
}

interface InviteInfo {
  role: UserRole;
  expiresAt: string;
  recoveryFor: PublicUser | null;
}

export function Invite() {
  const { route } = useLocation();
  const { params } = useRoute();
  const token = params.token!;
  const [info, setInfo] = useState<InviteInfo | null | undefined>(undefined);
  const { busy, error, run } = useAction();

  useEffect(() => {
    api<InviteInfo>('GET', `/api/auth/invite/${encodeURIComponent(token)}`)
      .then(setInfo)
      .catch(() => setInfo(null));
  }, [token]);

  if (info === undefined) return <Spinner />;
  if (info === null) {
    return (
      <AuthCard title="Invite expired">
        <p>This link has expired or was already used. Ask your admin for a new one.</p>
        <a class="button" href="/login">Go to sign in</a>
      </AuthCard>
    );
  }

  const register = (body: Record<string, unknown>) =>
    registerPasskey<{ user: PublicUser }>('/api/auth/invite/options', '/api/auth/invite/verify', {
      token,
      ...body,
    }).then(({ user: u }) => finishSignIn(route, u));

  if (info.recoveryFor) {
    return (
      <AuthCard title={`Welcome back, ${info.recoveryFor.displayName}`}>
        <p class="muted">
          Your old passkeys were removed. Create a new one on this device to get back into your
          account (<strong>{info.recoveryFor.username}</strong>).
        </p>
        <button
          type="button"
          class="button button-primary button-large"
          disabled={busy}
          onClick={() => run(() => register({}))}
        >
          {busy ? 'Waiting for passkey…' : 'Create passkey'}
        </button>
        {error && <p class="error">{error}</p>}
      </AuthCard>
    );
  }

  return (
    <AuthCard title="You're invited">
      <p class="muted">
        Create your account{info.role === 'admin' ? ' (admin)' : ''}. You'll sign in with a passkey
        instead of a password.
      </p>
      <NewAccountForm
        submitLabel="Create account"
        onSubmit={(username, displayName) => register({ username, displayName })}
      />
    </AuthCard>
  );
}
